/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import {
  previewChargeableWeightKg,
  previewVolumetricWeightKg,
} from '../../../main/API/shipmentPiece';

export const EMPTY_CREATE_PIECE_ROW = {
  description: '',
  weight_kg: '',
  width_cm: '',
  length_cm: '',
  height_cm: '',
  // Optional typed CBM/volumetric; blank lets the server compute from W*L*H.
  volumetric_weight_kg: '',
  note: '',
};

export function isCreatePieceRowEmpty(row) {
  if (!row || typeof row !== 'object') return true;
  return [
    row.description,
    row.weight_kg,
    row.width_cm,
    row.length_cm,
    row.height_cm,
    // A typed volumetric value alone is enough to keep the row.
    row.volumetric_weight_kg,
    row.note,
  ].every((v) => v == null || String(v).trim() === '');
}

export function validateCreatePieceRows(rows, t) {
  const errors = {};
  const nonEmpty = (rows || []).filter((row) => !isCreatePieceRowEmpty(row));
  if (!nonEmpty.length) {
    return {
      ok: false,
      errors: { _form: t('shipmentForm.createPiecesValAtLeastOne') },
      nonEmptyRows: [],
    };
  }

  nonEmpty.forEach((row) => {
    const index = rows.indexOf(row);
    const dims = [row.width_cm, row.length_cm, row.height_cm];
    const anyDim = dims.some((d) => d != null && String(d).trim() !== '');
    const allDim = dims.every((d) => d != null && String(d).trim() !== '');
    if (anyDim && !allDim) {
      errors[index] = {
        ...(errors[index] || {}),
        dimensions: t('shipmentPieces.dimensionsAllOrNoneHint', {
          defaultValue: 'Fill all three of W/L/H, or leave them all empty.',
        }),
      };
    }
  });

  return {
    ok: Object.keys(errors).length === 0,
    errors,
    nonEmptyRows: nonEmpty,
  };
}

export function buildCreatePiecePayload(row) {
  const payload = {};
  const strKeys = ['description', 'note'];
  // Blank volumetric_weight_kg is omitted so the server can still compute it.
  const numKeys = ['weight_kg', 'width_cm', 'length_cm', 'height_cm', 'volumetric_weight_kg'];

  strKeys.forEach((k) => {
    const v = row[k];
    if (v != null && String(v).trim() !== '') payload[k] = String(v).trim();
  });
  numKeys.forEach((k) => {
    const v = row[k];
    if (v != null && String(v).trim() !== '') payload[k] = String(v).trim();
  });
  return payload;
}

const DIM_FIELDS = [
  { key: 'width_cm', label: 'W', placeholder: 'W' },
  { key: 'length_cm', label: 'L', placeholder: 'L' },
  { key: 'height_cm', label: 'H', placeholder: 'H' },
];

function dimensionVolumetricPreview(row) {
  const dimsFilled = [row.width_cm, row.length_cm, row.height_cm].filter(
    (v) => v != null && String(v).trim() !== ''
  ).length;
  if (dimsFilled !== 3) return null;
  return previewVolumetricWeightKg(row.width_cm, row.length_cm, row.height_cm);
}

function PieceRowPreview({ row, t }) {
  const typedVol =
    row.volumetric_weight_kg != null && String(row.volumetric_weight_kg).trim() !== ''
      ? row.volumetric_weight_kg
      : null;
  // Typed volumetric wins over the W*L*H estimate, matching piece save().
  const vol = typedVol != null ? typedVol : dimensionVolumetricPreview(row);
  const chargeable = previewChargeableWeightKg(row.weight_kg, vol);
  if (chargeable == null) return null;
  return (
    <Chip size="sm" variant="soft" color="neutral" sx={{ maxWidth: '100%' }}>
      {t('shipmentForm.createPiecesChargeablePreview', {
        defaultValue: '≈ {{value}} kg chargeable',
        value: chargeable.toFixed(3),
      })}
    </Chip>
  );
}

export default function ShipmentCreatePiecesEditor({
  rows,
  onChange,
  fieldErrors = {},
  formError = '',
  disabled = false,
  disabledHint = '',
}) {
  const { t } = useTranslation('ui');
  const scrollContainerRef = useRef(null);
  const scrollToNewRowRef = useRef(false);

  const updateRow = useCallback(
    (index, patch) => {
      onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    },
    [rows, onChange]
  );

  const addRow = useCallback(() => {
    scrollToNewRowRef.current = true;
    onChange([...rows, { ...EMPTY_CREATE_PIECE_ROW }]);
  }, [rows, onChange]);

  useEffect(() => {
    if (!scrollToNewRowRef.current) return;
    scrollToNewRowRef.current = false;

    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    });
  }, [rows.length]);

  const removeRow = useCallback(
    (index) => {
      if (rows.length <= 1) {
        onChange([{ ...EMPTY_CREATE_PIECE_ROW }]);
        return;
      }
      onChange(rows.filter((_, i) => i !== index));
    },
    [rows, onChange]
  );

  const nonEmptyCount = rows.filter((row) => !isCreatePieceRowEmpty(row)).length;

  return (
    <Sheet
      variant="outlined"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overflowX: 'hidden',
        minHeight: { xs: 480, sm: 520 },
        maxHeight: { xs: 'min(85vh, 920px)', md: 'min(78vh, 860px)' },
        opacity: disabled ? 0.65 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 'md',
              bgcolor: 'primary.softBg',
              color: 'primary.plainColor',
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography level="title-md">
                {t('shipmentForm.createPiecesSectionTitle')}
              </Typography>
              <Chip size="sm" variant="soft" color="primary">
                {t('shipmentForm.createPiecesCount', { count: nonEmptyCount || rows.length })}
              </Chip>
            </Box>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {t('shipmentForm.createPiecesHint')}
            </Typography>
          </Box>
        </Box>

        {disabled && disabledHint ? (
          <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
            {disabledHint}
          </Alert>
        ) : null}

        {formError ? (
          <Alert variant="soft" color="danger" sx={{ mt: 1 }}>
            {formError}
          </Alert>
        ) : null}
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: { xs: 360, sm: 400 },
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          py: 1.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Stack spacing={1.5} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          {rows.map((row, index) => {
            const rowErrors = fieldErrors[index] || {};
            const dimsPartial =
              [row.width_cm, row.length_cm, row.height_cm].filter(
                (v) => v != null && String(v).trim() !== ''
              ).length > 0 &&
              [row.width_cm, row.length_cm, row.height_cm].some(
                (v) => v == null || String(v).trim() === ''
              );
            const volumetricPreview = dimensionVolumetricPreview(row);
            const volumetricPlaceholder =
              volumetricPreview != null ? `auto ≈ ${volumetricPreview.toFixed(3)}` : '';

            return (
              <Sheet
                key={`create-piece-${index}`}
                variant="soft"
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 'lg',
                  border: '1px solid',
                  borderColor: 'neutral.outlinedBorder',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Chip size="sm" variant="solid" color="primary">
                    {t('shipmentForm.createPiecesRowLabel', { index: index + 1 })}
                  </Chip>
                  <Box sx={{ flex: 1 }} />
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="danger"
                    onClick={() => removeRow(index)}
                    aria-label={t('shipmentForm.createPiecesRemoveRow')}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Grid container spacing={1.5} sx={{ width: '100%', maxWidth: '100%', m: 0 }}>
                  <Grid xs={12} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('actions.description')}
                    </Typography>
                    <Textarea
                      minRows={1}
                      maxRows={3}
                      size="sm"
                      sx={{ width: '100%' }}
                      value={row.description || ''}
                      onChange={(e) => updateRow(index, { description: e.target.value })}
                      placeholder={t('shipmentForm.createPiecesDescriptionPlaceholder')}
                    />
                  </Grid>

                  <Grid xs={12} sm={4} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.weight')}
                    </Typography>
                    <Input
                      size="sm"
                      type="number"
                      inputMode="decimal"
                      sx={{ width: '100%' }}
                      value={row.weight_kg || ''}
                      onChange={(e) => updateRow(index, { weight_kg: e.target.value })}
                      endDecorator="kg"
                      color={rowErrors.weight_kg ? 'danger' : 'neutral'}
                      slotProps={{ input: { min: 0, step: '0.001' } }}
                    />
                    {rowErrors.weight_kg ? (
                      <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                        {rowErrors.weight_kg}
                      </FormHelperText>
                    ) : null}
                  </Grid>

                  <Grid xs={12} sm={8} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.dimensions')}
                    </Typography>
                    <Sheet
                      variant="outlined"
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        p: 1,
                        borderRadius: 'md',
                        bgcolor: 'background.surface',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                          gap: 1,
                        }}
                      >
                        {DIM_FIELDS.map(({ key, label, placeholder }) => (
                          <Box key={key} sx={{ minWidth: 0 }}>
                            <Typography
                              level="body-xs"
                              sx={{ mb: 0.5, color: 'text.tertiary', textAlign: 'center' }}
                            >
                              {label}
                            </Typography>
                            <Input
                              size="sm"
                              type="number"
                              inputMode="decimal"
                              placeholder={placeholder}
                              sx={{ width: '100%' }}
                              value={row[key] || ''}
                              onChange={(e) => updateRow(index, { [key]: e.target.value })}
                              color={
                                rowErrors[key] || rowErrors.dimensions || dimsPartial
                                  ? 'danger'
                                  : 'neutral'
                              }
                              slotProps={{ input: { min: 0, step: '0.001' } }}
                            />
                          </Box>
                        ))}
                      </Box>
                      <Typography
                        level="body-xs"
                        sx={{ color: 'text.tertiary', mt: 0.75, textAlign: 'center' }}
                      >
                        cm
                      </Typography>
                    </Sheet>
                    {rowErrors.dimensions ? (
                      <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                        {rowErrors.dimensions}
                      </FormHelperText>
                    ) : dimsPartial && !rowErrors.dimensions ? (
                      <FormHelperText sx={{ color: 'warning.700', mt: 0.5 }}>
                        {t('shipmentPieces.dimensionsAllOrNoneHint')}
                      </FormHelperText>
                    ) : null}
                  </Grid>

                  <Grid xs={12} sm={6} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('shipmentPieces.volumetricWeightOverride', {
                        defaultValue: 'Volumetric weight (kg, optional)',
                      })}
                    </Typography>
                    <Input
                      size="sm"
                      type="number"
                      inputMode="decimal"
                      sx={{ width: '100%' }}
                      value={row.volumetric_weight_kg || ''}
                      onChange={(e) => updateRow(index, { volumetric_weight_kg: e.target.value })}
                      placeholder={volumetricPlaceholder}
                      endDecorator="kg"
                      color={rowErrors.volumetric_weight_kg ? 'danger' : 'neutral'}
                      slotProps={{ input: { min: 0, step: '0.001' } }}
                    />
                    <FormHelperText sx={{ color: 'text.tertiary', mt: 0.5 }}>
                      {t('shipmentPieces.volumetricAutoHint', {
                        defaultValue: 'Leave empty to let the server compute from W × L × H.',
                      })}
                    </FormHelperText>
                    {rowErrors.volumetric_weight_kg ? (
                      <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>
                        {rowErrors.volumetric_weight_kg}
                      </FormHelperText>
                    ) : null}
                  </Grid>

                  <Grid xs={12} sx={{ minWidth: 0 }}>
                    <PieceRowPreview row={row} t={t} />
                  </Grid>

                  <Grid xs={12} sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600 }}>
                      {t('actions.notes')}
                    </Typography>
                    <Textarea
                      minRows={1}
                      maxRows={2}
                      size="sm"
                      sx={{ width: '100%' }}
                      value={row.note || ''}
                      onChange={(e) => updateRow(index, { note: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Sheet>
            );
          })}
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.level1',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<AddIcon />}
            onClick={addRow}
            sx={{ flexShrink: 0 }}
          >
            {t('shipmentForm.createPiecesAddRow')}
          </Button>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', flex: 1, minWidth: 0 }}>
            {t('shipmentForm.createPiecesRollupHint')}
          </Typography>
        </Stack>
      </Box>
    </Sheet>
  );
}
